import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState } from 'react';
import styles from '../ReportCard.module.scss';
import { DateTime, Vehicle } from 'components/Erp/Display';
import { RefreshCw } from 'lucide-react';
import { canReadLoadPhotos } from '../utils/access';

import MiniMap from '../MiniMap';
import Pagination from './Pagination';
import DriverIdentity from './DriverIdentity';
import SearchBar from './SearchBar';
import { isFarFromBase } from '../utils/geo';

/**
 * Модальное окно истории перевозок.
 */
const HistoryModalView = ({
  arrivedBLoads,
  arrivedBCount,
  arrivedBNext,
  arrivedBPrev,
  arrivedBPage,
  setArrivedBPage,
  arrivedBSearch,
  setArrivedBSearch,
  historyLoading,
  historyTableLoading,
  loadHistory,
  onClose,
  user,
}) => {
  useInterfaceLanguage();
  const [visibleMaps, setVisibleMaps] = useState({});
  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={styles.historyModalContent}>
      <div className={styles.historyModalHeader}>
        <h2 className={styles.historyModalTitle}>{tr("История перевозок")}</h2>
        <button onClick={onClose} className={styles.closeButton}></button>
      </div>
      <div className={styles.historyModalBody}>
        {historyLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', fontSize: '16px' }}>
            <div style={{ marginBottom: '16px' }}>{tr("Загрузка истории...")}</div>
          </div>
        ) : (
          <>
            <SearchBar
              value={arrivedBSearch}
              onChange={setArrivedBSearch}
              onClear={() => { setArrivedBSearch(''); setArrivedBPage(1); }}
              placeholder={tr("Поиск по водителю, телефону, Базе, статусу, номеру машины...")}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (arrivedBPage === 1) loadHistory(false);
                  else setArrivedBPage(1);
                }
              }}
            />

            {/* Индикатор обновления */}
            <div style={{ height: '40px', marginBottom: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {tr(historyTableLoading && (
                <div style={{ padding: '8px', textAlign: 'center', background: "var(--theme-info-soft, #e7f3ff)", borderRadius: '6px', color: "var(--theme-info-text, #0d6efd)", fontWeight: '500', width: '100%' }}>
                  <RefreshCw size={14} aria-hidden="true" />{tr(" Обновление данных...")}</div>
              ))}
            </div>

            <Pagination
              page={arrivedBPage}
              setPage={setArrivedBPage}
              count={arrivedBCount}
              hasNext={!!arrivedBNext}
              hasPrev={!!arrivedBPrev}
              shownCount={arrivedBLoads.length}
            />

            <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}>
              <table className={styles.table_info}>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>{tr("Номер машины")}</th>
                    <th>{tr("Водитель")}</th>
                    <th>{tr("Статус")}</th>
                    <th>{tr("База")}</th>
                    <th>{tr("Модель машины")}</th>
                    <th>{tr("Время въезда (Кара-Кече)")}</th>
                    <th>{tr("Время выезда (Кара-Кече)")}</th>
                    <th>{tr("Время погрузки")}</th>
                    <th>{tr("Время прибытия (База)")}</th>
                    <th>{tr("Время доставки")}</th>
                    <th>{tr("Гео Въезд (Кара-Кече)")}</th>
                    <th>{tr("Гео Выезд (Кара-Кече)")}</th>
                    <th>{tr("Гео (База)")}</th>
                    <th>{tr("Отметил въезд")}</th>
                    <th>{tr("Отметил выезд")}</th>
                    <th>{tr("Отметил базу")}</th>
                    {tr((canReadLoadPhotos(user)) && <th>{tr("Фото точки Б")}</th>)}
                    <th>{tr("Примечания")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(arrivedBLoads) && arrivedBLoads.length > 0) ? arrivedBLoads.map((item, index) => {
                    const isFar = isFarFromBase(item.geo_b, item.geo_base);
                    const sequentialNumber = (arrivedBPage - 1) * 10 + index + 1;

                    return (
                      <tr key={item.id} className={isFar ? styles.dangerRow : ''}>
                        <td data-label={tr("№")}>{tr(sequentialNumber)}</td>
                        <td data-label={tr("Номер машины")}><Vehicle number={item.vehicle_number} model={item.vehicle_model} /></td>
                        <td data-label={tr("Водитель")}><DriverIdentity name={item.employee_name} phone={item.employee_phone} /></td>
                        <td data-label={tr("Статус")}>{
                          item.status === 'Въехал в точку А'
                            ? tr("Въехал в Кара-Кече")
                            : item.status === 'Въехал в точку Б'
                              ? tr("Доставил в Базу")
                              : tr(item.status)
                        }</td>
                        <td data-label={tr("База")}>{tr(item.top_base_name || item.chpp_name || item.private_point_name || '')}</td>
                        <td data-label={tr("Модель машины")}>{tr(item.vehicle_model || '-')}</td>
                        <td data-label={tr("Время въезда (Кара-Кече)")}><DateTime value={item.time_a} /></td>
                        <td data-label={tr("Время выезда (Кара-Кече)")}><DateTime value={item.time_a_exit} /></td>
                        <td data-label={tr("Время погрузки")}>{tr(item.duration_str_loading || '-')}</td>
                        <td data-label={tr("Время прибытия (База)")}><DateTime value={item.time_b} /></td>
                        <td data-label={tr("Время доставки")}>{tr(item.duration_str || '-')}</td>
                        <td data-label={tr("Гео Въезд (Кара-Кече)")}>
                          <GeoCell item={item} geoKey="geo_a" mapKey="geo_a_history" visibleMaps={visibleMaps} toggleMap={toggleMap} />
                        </td>
                        <td data-label={tr("Гео Выезд (Кара-Кече)")}>
                          <GeoCell item={item} geoKey="geo_a_exit" mapKey="geo_a_exit_history" visibleMaps={visibleMaps} toggleMap={toggleMap} />
                        </td>
                        <td data-label={tr("Гео (База)")}>
                          <GeoCell item={item} geoKey="geo_b" mapKey="geo_b" visibleMaps={visibleMaps} toggleMap={toggleMap} />
                        </td>
                        <td data-label={tr("Отметил въезд")}>{tr(item.guard_name || '')}{item.guard_name && item.guard_phone ? ' - ' : ''}{tr(item.guard_phone || '')}</td>
                        <td data-label={tr("Отметил выезд")}>{tr(item.exit_guard_name || '')}{item.exit_guard_name && item.exit_guard_phone ? ' - ' : ''}{tr(item.exit_guard_phone || '')}</td>
                        <td data-label={tr("Отметил базу")}>{tr(item.by_guard_name || '')}{item.by_guard_name && item.by_guard_phone ? ' - ' : ''}{tr(item.by_guard_phone || '')}</td>
                        {tr((canReadLoadPhotos(user)) && (
                          <td data-label={tr("Фото точки Б")}>
                            {item.photo_b ? (
                              <a href={item.photo_b} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={item.photo_b}
                                  alt={tr("Фото транспорта")}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: "2px solid var(--theme-info-border, #0d6efd)" }}
                                  title={tr("Нажмите для просмотра в полном размере")}
                                />
                              </a>
                            ) : (
                              <span style={{ color: "var(--theme-text-muted, #6c757d)" }}>-</span>
                            )}
                          </td>
                        ))}
                        <td data-label={tr("Примечания")}>
                          <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.notes || ''}>
                            {tr(item.notes || '-')}
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={(canReadLoadPhotos(user)) ? "19" : "18"} style={{ textAlign: 'center', padding: '20px' }}>{tr("Нет данных для отображения")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** Ячейка с геолокацией (кнопка Карты + MiniMap) */
const GeoCell = ({ item, geoKey, mapKey, visibleMaps, toggleMap }) => {
  useInterfaceLanguage();
  const geoValue = item[geoKey];
  const fullMapKey = `${item.id}-${mapKey}`;
  
  if (!geoValue) {
    return <span>-</span>;
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => window.open(`https://www.google.com/maps?q=${geoValue}`, '_blank')}
          className={styles.geoLinkBtn}
          title={tr("Открыть в Google Maps")}
        >{tr("Карты")}</button>
        <button
          onClick={() => toggleMap(item.id, mapKey)}
          className={styles.mapToggleBtn}
          title={tr("Показать/скрыть карту")}
        >
          {visibleMaps[fullMapKey] ? '' : ''}
        </button>
      </div>
      {tr(visibleMaps[fullMapKey] && <MiniMap geo={geoValue} />)}
    </div>
  );
};

export default React.memo(HistoryModalView);
